import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useESGPassportRegistryStore } from "../store/esgPassportRegistryStore";
import { useAIContext } from "../store/aiContextStore";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorBanner from "../components/common/ErrorBanner";

/**
 * ESG Passport Registry (v3)
 * 
 * UI for managing ESG passport registry, passport levels,
 * entity scoring, and registry health.
 */
export default function ESGPassportRegistry() {
  const { t } = useTranslation();
  const {
    passports,
    registryHealth,
    revokedCount,
    updateLatencyMS,
    issuePassport,
    revokePassport,
    exportPassportRegistry,
    getPassportLevelDistribution,
  } = useESGPassportRegistryStore();

  // Input state
  const [entityName, setEntityName] = useState("");
  const [esgScore, setEsgScore] = useState(0);
  const [region, setRegion] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Set active panel for AI context
  useEffect(() => {
    useAIContext.getState().setActivePanel("PassportRegistryPanel");
  }, []);

  // Update AI context inputs on change
  useEffect(() => {
    const distribution = getPassportLevelDistribution();
    useAIContext.getState().setInput("registryHealth", registryHealth);
    useAIContext.getState().setInput("revokedCount", revokedCount);
    useAIContext.getState().setInput("updateLatencyMS", updateLatencyMS);
    useAIContext.getState().setInput("passportCount", passports.length);
    useAIContext.getState().setInput("passportLevelDistribution", distribution);
  }, [registryHealth, revokedCount, updateLatencyMS, passports.length]);

  // Derived values - compute when needed for rendering (after useEffect)
  const passportLevelDistribution = getPassportLevelDistribution();

  const handleIssuePassport = async () => {
    if (!entityName || esgScore === 0) {
      setError(new Error(t("passport.invalidInput") || "Entity name and ESG score are required"));
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      await issuePassport(entityName, esgScore, region);
      setEntityName("");
      setEsgScore(0);
      setRegion("");
    } catch (e) {
      const error = e instanceof Error ? e : new Error(t("passport.issueFailed") || "Failed to issue passport");
      setError(error);
      console.error("Issue passport failed:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      await revokePassport(id);
    } catch (e) {
      const error = e instanceof Error ? e : new Error(t("passport.revokeFailed") || "Failed to revoke passport");
      setError(error);
      console.error("Revoke passport failed:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (passports.length === 0) {
      setError(new Error(t("passport.noPassports") || "No passports to export"));
      return;
    }
    
    setError(null);
    try {
      exportPassportRegistry();
    } catch (e) {
      const error = e instanceof Error ? e : new Error(t("passport.exportFailed") || "Failed to export passport registry");
      setError(error);
      console.error("Export failed:", e);
    }
  };

  return (
    <div className="space-y-6 text-white" style={{ minHeight: "400px" }}>
      <div className="font-orbitron text-evat-neon text-xl mb-4">
        {t("passport.title") || "ESG Passport Registry"}
      </div>

      <ErrorBanner error={error} onRetry={() => setError(null)} showRetry={!!error} />
      
      {loading && <LoadingSpinner size="md" text={t("passport.processing") || "Processing..."} />}

      {/* Input Controls */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("passport.entityName") || "Entity Name"}
          </label>
          <input
            type="text"
            value={entityName}
            onChange={(e) => setEntityName(e.target.value)}
            placeholder="e.g., Company ABC"
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("passport.esgScore") || "ESG Score (0-100)"}
          </label>
          <input
            type="number"
            min="0"
            max="100"
            value={esgScore}
            onChange={(e) => setEsgScore(Number(e.target.value))}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("passport.region") || "Region"}
          </label>
          <input
            type="text"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            placeholder="e.g., EU, EEA, National"
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <button
          onClick={handleIssuePassport}
          disabled={!entityName || esgScore === 0 || loading}
          className="px-4 py-2 bg-evat-gold/20 border border-evat-gold text-evat-gold rounded hover:bg-evat-gold/30 transition-colors font-orbitron disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (t("passport.processing") || "Processing...") : (t("passport.issue") || "Issue Passport")}
        </button>
      </div>

      {/* Passports List */}
      {passports.length > 0 && (
        <div className="space-y-2 mt-6">
          <div className="font-orbitron text-evat-gold text-sm mb-2">
            {t("passport.passports") || "Passports"} ({passports.length})
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {passports.map((passport) => (
              <div
                key={passport.id}
                className="border border-evat-gold/30 rounded p-3 bg-black/50 flex justify-between items-center"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="text-sm font-orbitron text-evat-neon">
                      {passport.entityName}
                    </div>
                    <div className={`text-xs px-2 py-1 rounded ${
                      passport.passportLevel === "sovereign" ? "bg-gold-500/20 text-evat-gold border border-evat-gold/40" :
                      passport.passportLevel === "enhanced" ? "bg-blue-500/20 text-blue-400 border border-blue-500/40" :
                      "bg-neutral-500/20 text-neutral-400 border border-neutral-500/40"
                    }`}>
                      {passport.passportLevel.toUpperCase()}
                    </div>
                  </div>
                  <div className="text-xs text-neutral-400">
                    {passport.region} • ESG: {passport.esgScore}% • Last Audit: {new Date(passport.lastAuditISO).toLocaleDateString()}
                  </div>
                </div>
                <button
                  onClick={() => handleRevoke(passport.id)}
                  disabled={loading}
                  className="ml-4 px-2 py-1 text-xs bg-red-500/20 border border-red-500/40 text-red-400 rounded hover:bg-red-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t("passport.revoke") || "Revoke"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Metrics Display */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("passport.registryHealth") || "Registry Health"}
          </div>
          <div className={`text-lg font-orbitron ${
            registryHealth >= 80 ? "text-green-400" :
            registryHealth >= 50 ? "text-yellow-400" :
            "text-red-400"
          }`}>
            {registryHealth.toFixed(1)}%
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("passport.revokedCount") || "Revoked Count"}
          </div>
          <div className="text-lg font-orbitron text-red-400">
            {revokedCount}
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("passport.updateLatencyMS") || "Update Latency"}
          </div>
          <div className="text-lg font-orbitron text-evat-neon">
            {updateLatencyMS}ms
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("passport.totalPassports") || "Total Passports"}
          </div>
          <div className="text-lg font-orbitron text-evat-gold">
            {passports.length}
          </div>
        </div>
      </div>

      {/* Passport Level Distribution */}
      {passports.length > 0 && (
        <div className="grid grid-cols-3 gap-4 mt-4">
          <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
            <div className="text-xs text-neutral-400 mb-1">
              {t("passport.basic") || "Basic"}
            </div>
            <div className="text-sm font-orbitron text-neutral-400">
              {passportLevelDistribution.basic}
            </div>
          </div>

          <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
            <div className="text-xs text-neutral-400 mb-1">
              {t("passport.enhanced") || "Enhanced"}
            </div>
            <div className="text-sm font-orbitron text-blue-400">
              {passportLevelDistribution.enhanced}
            </div>
          </div>

          <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
            <div className="text-xs text-neutral-400 mb-1">
              {t("passport.sovereign") || "Sovereign"}
            </div>
            <div className="text-sm font-orbitron text-evat-gold">
              {passportLevelDistribution.sovereign}
            </div>
          </div>
        </div>
      )}

      {/* Export Button */}
      <div className="fixed bottom-4 right-4">
        <button
          onClick={handleExport}
          disabled={passports.length === 0 || loading}
          className="px-4 py-2 bg-evat-gold/20 border border-evat-gold text-evat-gold rounded hover:bg-evat-gold/30 transition-colors font-orbitron disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={t("passport.export") || "Export Passport Registry"}
        >
          {t("passport.export") || "Export Passport Registry"}
        </button>
      </div>
    </div>
  );
}

